<template>
  <v-app id="app">
    <v-progress-linear :value="progress" color="primary"></v-progress-linear>
    <v-container fluid class="pa-0">
      <v-row no-gutters>
        <v-col
          sm="12"
          md="6"
          style="position: relative;"
        >
          <div style="width: 100%; height: 100%; overflow: hidden; position: absolute; top: 0; left: 0;">
            <img :src="require('./assets/pattern-top.png')" style="position: absolute; top: 0; left: 0; width: 100%; z-index: 999;">
            <img :src="require('./assets/pattern-bottom.png')" style="position: absolute; bottom: 0; right: 0; width: 100%; z-index: 999;">
          </div>
          <splide 
            ref="imagesSplide"
            :options="imagesSplideOptions"
            :key="`imagesSplide_${windowInnerWidth}`"
          >
            <splide-slide 
              v-for="image in visibleQuestionsImages"
              :key="image.id"
            >
              <img :data-splide-lazy="image.imageSrc" style="width: 100%; height: 100%; object-fit: cover;" />
            </splide-slide>
          </splide>
        </v-col>
        <v-col
          sm="12"
          md="6"
        >
          <splide 
            ref="questionsSplide"
            class="splide__slide--secondary"
            :key="`questionsSplide_${windowInnerWidth}`"
            :options="questionsSplideOptions"
          >
            <splide-slide 
              v-for="(question, questionIndex) in visibleQuestions"
              :key="question.id"
              :class="question.background"
              class="pa-lg-14 pa-md-6 pa-sm-8 pa-xs-6"
            >
              <v-form v-model="questionsValidity[question.id]" ref="forms" style="width: 100%;">
                <v-container fluid>
                  <v-row>
                    <v-col v-if="question.group || question.text">
                      <p class="question__number text-left" v-html="question.group"></p>            
                      <div class="question__text question__text--large text-left" v-html="question.text"></div>
                    </v-col>
                  </v-row>
                  <v-row v-for="field in question.fields" :key="field.id">
                    <v-col>
                      <v-autocomplete 
                        v-model.lazy="questionsAnswers[field.id]" 
                        v-if="field.vComponent === 'v-autocomplete'"
                        :items="field.options"
                        :label="field.label"
                        :rules="field.rules"
                        hide-details="auto"
                        dark
                      ></v-autocomplete>
                      <v-text-field
                        v-model.lazy="questionsAnswers[field.id]" 
                        v-if="field.vComponent === 'v-text-field'"
                        :label="field.label"
                        :rules="field.rules"
                        hide-details="auto"
                        dark
                      ></v-text-field>
                      <v-radio-group 
                        v-model.lazy="questionsAnswers[field.id]" 
                        v-else-if="field.vComponent === 'v-radio'"
                        :label="field.label"
                        :rules="field.rules"
                        hide-details="auto"
                        dark
                      >
                        <v-radio
                          v-for="option in field.options"
                          :key="option.value"
                          :value="option.value"
                          :label="option.label"
                          color="primary"
                          dark
                        ></v-radio>
                      </v-radio-group>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col class="d-flex mt-2">
                      <v-btn
                        class="mr-2" 
                        tile
                        outlined
                        dark
                        v-if="questionIndex !== 0 && questionIndex < visibleQuestions.length - 1"
                        @click="previousSlide"
                      >
                        Previous
                      </v-btn>
                      <v-btn
                        tile
                        large
                        dark
                        v-if="questionIndex === 0"
                        @click="startQuiz"
                      >
                        Start
                      </v-btn>
                      <v-btn
                        tile
                        dark
                        color="primary"
                        v-else-if="questionIndex < visibleQuestions.length - 2"
                        :disabled="!questionsValidity[question.id]"
                        @click="nextSlide"
                      >
                        Next
                      </v-btn>
                      <v-btn
                        tile
                        dark
                        color="primary"
                        v-else-if="questionIndex === visibleQuestions.length - 2"
                        :disabled="!questionsValidity[question.id]"
                        @click="checkResult"
                      >
                        Check result
                      </v-btn>
                      <v-btn
                        tile
                        color="white"
                        href="https://www.pwc.com/c1/en/esg-self-assessment-results.html"
                        target="_blank"
                        v-else
                      >
                        Find out more
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row v-if="question.footer">
                    <v-col>
                      <div class="text-caption" v-html="question.footer"></div>
                    </v-col>
                  </v-row>
                </v-container>
              </v-form>
            </splide-slide>
          </splide>
        </v-col>
      </v-row>
      <div v-if="!isProd">
        <p>{{ `Version: ${appVersion}` }}</p>
        <p>{{ `Most frequent answer: ${mostFrequentAnswer}` }}</p>
        <p>{{ questionsAnswers }}</p>
      </div>
    </v-container>
  </v-app>
</template>

<script>
import "@splidejs/splide/dist/css/themes/splide-default.min.css";
import Vue from 'vue'
import { Splide, SplideSlide } from "@splidejs/vue-splide";

export default {
  name: "App",
  components: {
    Splide,
    SplideSlide,
  },
  data() {
    return {
      isProd: process.env.NODE_ENV === 'production',
      appVersion: process.env.VUE_APP_VERSION,
      baseUrl: process.env.BASE_URL,
      id: null,
      progress: 0,
      imagesSplide: null,
      questionsSplide: null,
      splideDelay: 50,
      splideOptions: {
        type: 'loop',
        drag: false,
        arrows: false,
        pagination: false,
        keyboard: false,
        speed: 900
      },
      mobileBreakpoint: 768,
      isSliding: false,
      questionsAnswers: {},
      questionsValidity: {},
      mostFrequentAnswer: null,
      windowInnerWidth: 0
    };
  },
  computed: {
    questions() {
      return [
        {
          imageSrc: require('./assets/cover.jpeg'),
          group: "",
          text: `
            <h3 class="text-h3 mb-8 pr-4" style="font-family: 'Charter ITC', Georgia, serif!important;">ESG Self-Assessment</h3>
            <p class="text-subtitle-1 pr-4" style="font-family: inherit!important;">ESG isn’t about checking a box. It’s about transforming your business and approach to focus on a new set of outcomes. One that continuously creates value and builds trust for business, economy and society.</p>
            <p class="text-subtitle-1 pr-4" style="font-family: inherit!important;">How do you get there from here? You need a realistic path.</p>
            <p class="text-subtitle-1 pr-4" style="font-family: inherit!important;">We have designed this tool to help you assess your starting point and map the way forward.</p>
            <p class="text-subtitle-1 pr-4" style="font-family: inherit!important;">The tool looks at 21 key features of ESG programme planning. Choose the statement under each question that best matches the current situation at your organisation. Upon completing the self-assessment you will receive a starting point profile and invitation to discuss suggested next steps.</p>
          `,
          background: "primary",
          footer: "",
          isVisible: true,
          fields: [],
        },
        {
          imageSrc: require('./assets/metrics.jpeg'),
          group: "",
          text: "First, tell us about your organisation",
          background: "",
          footer: `
            <p>The information obtained in the assessment will be only used for the purposes of creating reports and benchmarking. All of the information is transferred to PwC anonymously.</p>
          `,
          isVisible: true,
          fields: [
            {
              id: "JobPosition",
              vComponent: "v-autocomplete",
              label: "Your position in company",
              rules: [
                v => !!v || "This field is required."
              ],
              isVisible: true,
              isCached: true,
              options: [
                "Intern/Assistant",
                "Senior specialist/Specialist",
                "Chief specialist",
                "Manager",
                "Director",
                "Board member",
                "Owner"
              ],
            },
            {
              id: "Industry",
              vComponent: "v-autocomplete",
              label: "Industry",
              rules: [
                v => !!v || "This field is required."
              ],
              isVisible: true,
              isCached: true,
              options: [
                "Aerospace and Defence",
                "Asset and Wealth Management",
                "Automotive",
                "Banking and Capital Markets",
                "Business Services",
                "Central, National, Federal Government",
                "Chemicals",
                "Consumer",
                "Energy",
                "Engineering and Construction",
                "Forest, Paper and Packaging",
                "Government & Public Sector",
                "Health Services",
                "Hospitality and Leisure",
                "Insurance",
                "Intl Government, Organisations",
                "Manufacturing",
                "Media and Entertainment",
                "Metals and Mining",
                "Pharma and Life Sciences",
                "Power and Utilities",
                "Private Equity",
                "Regional, State, Local and City Government",
                "Retail",
                "Sovereign Investment Funds",
                "Technology",
                "Telecommunications",
                "Transportation and Logistics"
              ],
            },
            {
              id: "YearlyTurnover",
              vComponent: "v-autocomplete",
              label: "Yearly turnover (EUR / net)",
              rules: [
                v => !!v || "This field is required."
              ],
              isVisible: true,
              isCached: true,
              options: [
                'Up to 2m',
                '2-10m',
                '10-50m',
                'Above 50m'
              ],
            },
            {
              id: "SizeOfCompany",
              vComponent: "v-autocomplete",
              label: "Size of the company (employment)",
              rules: [
                v => !!v || "This field is required."
              ],
              isVisible: true,
              isCached: true,
              options: [
                "0-10",
                "11-50",
                "101-250",
                "250+"
              ],
            },
            {
              id: "Country",
              vComponent: "v-autocomplete",
              label: "Country",
              rules: [
                v => !!v || "This field is required."
              ],
              isVisible: true,
              isCached: true,
              options: [
                "Afghanistan",
                "Aland Islands",
                "Albania",
                "Algeria",
                "American Samoa",
                "Andorra",
                "Angola",
                "Anguilla",
                "Antarctica",
                "Antigua and Barbuda",
                "Argentina",
                "Armenia",
                "Aruba",
                "Australia",
                "Austria",
                "Azerbaijan",
                "Bahamas",
                "Bahrain",
                "Bangladesh",
                "Barbados",
                "Belarus",
                "Belgium",
                "Belize",
                "Benin",
                "Bermuda",
                "Bhutan",
                "Bolivia",
                "Bonaire",
                "Bosnia and Herzegovina",
                "Botswana",
                "Bouvet Island",
                "Brazil",
                "British Antarctic Territory",
                "British Indian Ocean Territory",
                "Brunei Darussalam",
                "Bulgaria",
                "Burkina Faso",
                "Burma",
                "Burundi",
                "Byelorussian SSR",
                "Cambodia",
                "Cameroon",
                "Canada",
                "Canton and Enderbury Islands",
                "Cape Verde",
                "Cayman Islands",
                "Central African Republic",
                "CES - unknown",
                "Chad",
                "Chile",
                "China",
                "Christmas Island",
                "Cocos (Keeling) Islands",
                "Colombia",
                "Comoros",
                "Congo",
                "Congo, Democratic Republic Of The",
                "Cook Islands",
                "Costa Rica",
                "Croatia",
                "Cuba",
                "Curacao",
                "Cyprus",
                "Czech Republic",
                "Denmark",
                "Djibouti",
                "Dominica",
                "Dominican Republic",
                "Dronning Maud Land",
                "East Timor",
                "Ecuador",
                "Egypt",
                "El Salvador",
                "Equatorial Guinea",
                "Eritrea",
                "Estonia",
                "Ethiopia",
                "Falkland Islands",
                "Faroe Islands",
                "Fiji",
                "Finland",
                "France",
                "French Afars and Issas",
                "French Guiana",
                "French Polynesia",
                "French Southern Territories",
                "Gabon",
                "Gambia",
                "Georgia",
                "German Democratic Republic",
                "Germany",
                "Ghana",
                "Gibraltar",
                "Gilbert and Ellice Islands",
                "Greece",
                "Greenland",
                "Grenada",
                "Guadeloupe",
                "Guam",
                "Guatemala",
                "Guernsey",
                "Guinea",
                "Guinea-Bissau",
                "Guyana",
                "Haiti",
                "Heard Island and Mcdonald Islands",
                "Honduras",
                "Hong Kong",
                "Hungary",
                "Iceland",
                "India",
                "Indonesia",
                "Iran",
                "Iraq",
                "Ireland",
                "Isle of Man",
                "Israel",
                "Italy",
                "Ivory Coast",
                "Jamaica",
                "Japan",
                "Jersey",
                "Johnston Island",
                "Jordan",
                "Kazakhstan",
                "Kenya",
                "Kiribati",
                "Kosovo",
                "Kuwait",
                "Kyrgyzstan",
                "Laos",
                "Latvia",
                "Lebanon",
                "Lesotho",
                "Liberia",
                "Libya",
                "Liechtenstein",
                "Lithuania",
                "Luxembourg",
                "Macao",
                "Madagascar",
                "Malawi",
                "Malaysia",
                "Maldives",
                "Mali",
                "Malta",
                "Marshall Islands",
                "Martinique",
                "Mauritania",
                "Mauritius",
                "Mayotte",
                "Mexico",
                "Micronesia",
                "Midway Islands",
                "Moldova",
                "Monaco",
                "Mongolia",
                "Montenegro",
                "Montserrat",
                "Morocco",
                "Mozambique",
                "Myanmar",
                "Namibia",
                "Nauru",
                "Nepal",
                "Netherlands",
                "Netherlands Antilles",
                "Neutral Zone",
                "New Caledonia",
                "New Hebrides",
                "New Zealand",
                "Nicaragua",
                "Niger",
                "Nigeria",
                "Niue",
                "Norfolk Island",
                "North Korea",
                "North Macedonia",
                "Northern Mariana Islands",
                "Norway",
                "Oman",
                "Pacific Islands (Trust Territory)",
                "Pakistan",
                "Palau",
                "Palestine",
                "Panama",
                "Panama Canal Zone",
                "Papua New Guinea",
                "Paraguay",
                "Peru",
                "Philippines",
                "Pitcairn",
                "Poland",
                "Portugal",
                "Puerto Rico",
                "Qatar",
                "Reunion",
                "Romania",
                "Russian Federation",
                "Rwanda",
                "Saint Barthelemy",
                "Saint Helena",
                "Saint Kitts and Nevis",
                "Saint Lucia",
                "Saint Martin (Dutch part)",
                "Saint Martin (French part)",
                "Saint Pierre and Miquelon",
                "Saint Vincent and The Grenadines",
                "Samoa",
                "San Marino",
                "Sao Tome and Principe",
                "Saudi Arabia",
                "Senegal",
                "Serbia",
                "Serbia and Montenegro",
                "Seychelles",
                "Sierra Leone",
                "Sikkim",
                "Singapore",
                "Slovakia",
                "Slovenia",
                "Solomon Islands",
                "Somalia",
                "South Africa",
                "South Georgia and The South Sandwich Islands",
                "South Korea",
                "South Sudan",
                "Southern Rhodesia",
                "Spain",
                "Sri Lanka",
                "Sudan",
                "Suriname",
                "Svalbard and Jan Mayen",
                "Swaziland",
                "Sweden",
                "Switzerland",
                "Syrian Arab Republic",
                "Taiwan",
                "Tajikistan",
                "Tanzania",
                "Thailand",
                "Timor-Leste",
                "Togo",
                "Tokelau",
                "Tonga",
                "Trinidad and Tobago",
                "Tunisia",
                "Turkey",
                "Turkmenistan",
                "Turks and Caicos Islands",
                "Tuvalu",
                "Uganda",
                "Ukraine",
                "United Arab Emirates",
                "United Kingdom",
                "United States",
                "United States Minor Outlying Islands",
                "United States Miscellaneous Pacific Islands",
                "Uruguay",
                "USSR",
                "Uzbekistan",
                "Vanuatu",
                "Vatican City",
                "Venezuela",
                "Viet Nam",
                "Viet-Nam, Democratic Republic of",
                "Virgin Islands (British)",
                "Virgin Islands (U.S.)",
                "Wake Island",
                "Wallis and Futuna",
                "Western Sahara",
                "Yemen",
                "Yemen, Democratic",
                "Yugoslavia",
                "Zaire",
                "Zambia",
                "Zimbabwe"
              ],
            },
          ],
        },
        /* {
          imageSrc: require('./assets/metrics.jpeg'),
          group: "",
          text: "Please leave your personal details if you are are interested in hearing from us",
          background: "",
          footer: `
            <p>The information obtained in this part of the assessment will be only used for the purposes of creating reports and benchmarking. All of the information is transferred to PwC anonymously.</p>
            <p>PwC Advisory spółka z ograniczoną odpowiedzialnością sp.k. or another PwC entity from which you receive commercial information - <a href="https://www.pwc.com/c1/en/CEE-legal-entities.html" target="_blank">list of entities</a>, with its registered seat in Warsaw (00-633), Polna 11 Street will be the controller of your personal data. The data will be processed for the purpose of direct marketing through sending commercial information to you via email. Full information about processing your personal data is available in our <a href="https://www.pwc.pl/pl/o-nas/polityka-prywatnosci/newsletter.html" target="_blank">Privacy Policy</a>.</p>
          `,
          isVisible: true,
          fields: [
            {
              id: "FullName",
              vComponent: "v-text-field",
              label: "Full Name (optional)",
              rules: [
                v => (!v || v && v.length <= 255) || "Must not be longer than 255 characters."
              ],
              isVisible: true,
              isCached: true,
              options: [],
            },
            {
              id: "Email",
              vComponent: "v-text-field",
              label: "Email (optional)",
              rules: [ 
                v => (!v || v && /(?:[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[A-Za-z0-9-]*[A-Za-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/g
                  .test(v)) || 'Please provide a valid email address',
                v => (!v || v && v.length <= 255) || "Must not be longer than 255 characters."
              ],
              isVisible: true,
              isCached: true,
              options: [],
            },
            {
              id: "BusinessTitle",
              vComponent: "v-text-field",
              label: "Business title (optional)",
              rules: [
                v => (!v || v && v.length <= 255) <= 255 || "Must not be longer than 255 characters."
              ],
              isVisible: true,
              isCached: true,
              options: [],
            },
            {
              id: "Company",
              vComponent: "v-text-field",
              label: "Company (optional)",
              rules: [ 
                v => (!v || v && v.length <= 255) <= 255 || "Must not be longer than 255 characters."
              ],
              isVisible: true,
              isCached: true,
              options: [],
            },
          ],
        }, */
        {
          imageSrc: require('./assets/q1.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 1 of 21
            </span>
            </br>
            Programme Governance
          `,
          text: "Clear ESG Governance Structure",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q1",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "There is no ESG governance structure in place within the Organisation.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "There is an informal governance structure on ESG matters that is not incorporated in the company policy.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "There is a formal governance structure on ESG matters, with a predominant focus on conventional environmental, social and governance topics.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "There is a clear governance structure in place within the Organisation as to roles, responsibilities and KPIs, with a clear ESG charter setting out the Group's/Company's obligations and commitments on ESG topics and an assessment of opportunities and risks.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q2.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 2 of 21
            </span>
            </br>
            Programme Governance
          `,
          text: "Board Leadership",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q2",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "The Board is not involved in ESG matters.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "Leadership is delegated to 1 board member.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "Dedicated Board sub-committee for ESG matters.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "The Board takes clear active leadership on the ESG agenda and regularly discusses risk and opportunities. The ESG agenda and company plans are communicated internally throughout the organisation.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q3.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 3 of 21
            </span>
            </br>
            Programme Governance
          `,
          text: "Board Access to Expertise",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q3",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "The Board has no access to internal external expertise related to ESG and does not envisage needing or utilising such expertise (internally nor externally) in the future. The Board has little experience with ESG topics.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "The Board has limited experience in ESG matters.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "The Board has a growing expertise in ESG matters and utilises some access to outside support on an ad hoc basis.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "The Board has strong and growing expertise in ESG topics. The Board has and utilises access to internal and external expertise related to ESG topics on a regular basis to inform themselves of developments and potential opportunities and risks.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q4.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 4 of 21
            </span>
            </br>
            Programme Governance
          `,
          text: "Periodic Reporting to the Board",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q4",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "There is little or no periodic reporting to the Board except on conventional ESG matters such as health and safety at work.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "There is limited reporting to the Board on an ad-hoc basis and not focused on specific areas.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "There is some periodic reporting to the Board on ESG matters focused predominantly on business matters.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "There is periodic reporting to the Board on key ESG topics including full disclosure of ESG issues and risks, ESG programme developments with regards to goals and KPIs and actions required to be implemented.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q5.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 5 of 21
            </span>
            </br>
            Programme Governance
          `,
          text: "ESG Leadership Appointed",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q5",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "There is no clear ESG leader within the Organisation.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "Distributed leadership to more roles in the Organisation.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "There is a leader within the Organisation for ESG matters who has limited authority and works through influence with other business units.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "The Organisation has appointed a clear ESG leader (Chief Sustainability Officer), with clear KPIs and targets, who has the role and authority to drive the ESG programme, reports to the Board and makes decisions.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q6.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 6 of 21
            </span>
            </br>
            Project Management
          `,
          text: "Programme Manager Appointed",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q6",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "There is no manager appointed to run the ESG agenda / programme in the organisation.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "A junior person has been designated to drive the ESG agenda / programme.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "A senior manager has been designated to drive the ESG agenda / programme, but only part of his / her capacity is dedicated to ESG.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "One senior manager fully dedicated to ESG has been appointed with authority and responsibility to lead and organise the ESG programme across the Organisation.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q7.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 7 of 21
            </span>
            </br>
            Project Management
          `,
          text: "Investor Alignment",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q7",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "The Organisation has few interactions with investors and analysts / other stakeholders and these interactions do not fully cover ESG topics but rather more conventional financial / business topics.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "There is limited disclosure reporting on ESG matters in external non-financial reports and the Organisation responds to investors and analysts in reactive mode.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "There is disclosure by the Organisation is providing disclosures on ESG matters to investors and there is more active discussion with investors and analysts.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "The Board engages actively with stakeholders and investors on the ESG agenda, considering feedback and insights provided by investors in its plans and strategic investments. Active discussion with analysts.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q8.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 8 of 21
            </span>
            </br>
            Stakeholder Support
          `,
          text: "Employee Involvement and Support",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q8",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "The Organisation does not address ESG matters with its employees and does not solicit feedback or input from its employees.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "Employees are involved, but mostly in community actions and social matters.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "The Board sponsors initiatives in the ESG space and communicates on such initiatives internally with employees.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "The Organisation extensively communicates with its employees on the importance of ESG, the Organisation's ESG programme and how employees can be involved in and support the ESG aspirations of the Organisation.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q9.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 9 of 21
            </span>
            </br>
            Stakeholder Support
          `,
          text: "Understanding of Regulatory Compliance Requirements",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q9",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "The Board and management have a low understanding of the risk management and regulatory environment affecting their business with regard to ESG topics and are not taking a coordinated approach to ensuring regulatory compliance.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "The Organisation has a limited understanding of the risk and regulatory impacts of ESG and is implementing change based on individual directives rather than having a comprehensive approach.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "The Board and management are actively dealing with the ESG risks and regulatory requirements with a good understanding of the implications of the regulations.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "The Board and management have strong expertise in the ESG risks  management and regulations areas relating to ESG as they impact the business, and have clear and comprehensive programmes in place to ensure proper risk management and regulatory compliance.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q10.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 10 of 21
            </span>
            </br>
            Rating Agencies
          `,
          text: "Rating Agency Information Accessible",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q10",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "The organisation doesn’t disclose information on its ESG impact and agenda and doesn’t have an ESG rating (or has a very low score).",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "The Organisation provides limited information on ESG in its non-financial reporting, and does not report against a known framework such as TCFD or GRI.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "The Organisation includes significant information on ESG matters in its non-financial reporting but has limited interaction with rating agencies.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "The Organisation has solicited an ESG rating from a recognized rating agency and the management communicates regularly with rating agencies.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q11.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 11 of 21
            </span>
            </br>
            Targets and Metrics
          `,
          text: "Comprehensive Suite of Goals",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q11",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "The Organisation has no targets and goals in relation to ESG commitments.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "The Organisation has some goals and targets relating to ESG but these are not well organised and are not comprehensive.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "The Organisation has a well defined set of goals and targets relating to ESG.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "The Organisation has a set of comprehensive targets and goals that it wishes to achieve within defined, has deadlines, and communicates such targets and goals publicly and within the organisation, including the KPIs.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q12.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 12 of 21
            </span>
            </br>
            Carbon Footprint
          `,
          text: "Carbon Footprint Reduction Programme and Goals",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q12",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "The Organisation has no Carbon Footprint Reduction programme in place, with no specific targets for reducing the business's carbon footprint, and no goals publicly communicated.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "The Organization has decided and published modest carbon footprint reduction goals. Individual measures are being taken to reduce the carbon footprint.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "The Organization has decided and published ambitious carbon footprint reduction goals. A comprehensive programme has been established to attain the goals.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "The Organisation has publicly communicated a Net Zero target and commitments, and has a comprehensive plan and resources in place to achieve them, including reporting on progress on a regular basis.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q13.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 13 of 21
            </span>
            </br>
            Data Management
          `,
          text: "Understanding of Data Required for ESG Management",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q13",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "Management has not determined its data needs for achieving its ESG goals, and has not put in place data management and reporting so as to have a clear perspective on how they are progressing to achieve the ESG goals and targets.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "The Organisation has started initial steps to understand the ESG data requirements for internal management purposes and is starting to collect data internally.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "The Organisation has a good understanding of the data it requires for internal reporting and is gathering the data.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "Management has determined its data needs for achieving its ESG goals, and has put in place data management and regular reporting so as to have a clear perspective on how they are progressing to achieve the ESG goals and targets.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q14.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 14 of 21
            </span>
            </br>
            Data Management
          `,
          text: "Understanding of Data Required for External Reporting",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q14",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "The Organisation has not assessed what data is required for external reporting of ESG matters (NFRD, GRI, TCFD etc).",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "The Organisation has a limited understanding of the data required for external reporting, is gathering such data and reporting in a limited way.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "The Organisation has a good understanding of the data required for external reporting and is reporting externally.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "The Organisation has assessed what data is required for external reporting of ESG matters (NFRD, GRI, TCFD etc) and has put in place processes to be able to publish external reports in high quality and detail.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q15.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 15 of 21
            </span>
            </br>
            Reporting & Disclosure
          `,
          text: "Actual Disclosure of ESG Plans and Results",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q15",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "There is no external reporting of ESG matters done by the Organisation.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "There is very limited external reporting on ESG done by the Organisation.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "The Organisation is starting to report in accordance with an established reporting framework like TCFD and GRI.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "The Organisation reports externally in a comprehensive manner in compliance with various non-governmental reporting frameworks and other reporting obligations like TCFD and GRI.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q16.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 16 of 21
            </span>
            </br>
            Reporting & Disclosure
          `,
          text: "ESG Communications Strategy",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q16",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "The Organisation has no formal, comprehensive communications strategy internally or externally with regard to ESG matters.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "There is a limited communications plan in place for internal and external reporting.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "There is a good communications strategy in place for external and internal communications on ESG matters.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "The Organisation has a formal, comprehensive and active communications strategy internally or externally with regard to ESG matters.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q17.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 17 of 21
            </span>
            </br>
            Programme Assurance
          `,
          text: "Validation of ESG Data Reported",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q17",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "There is no independent external validation of the ESG information and data reported publicly by the Organisation, or no information is reported.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "The Organisation is considering the use of external auditors to validate the information published in the non-financial disclosures.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "The Organisation has its external ESG reporting verified / audited by an external auditor.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "The Organisation has its public communications and reporting on ESG independently reviewed and verified by an external specialised company.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q18.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 18 of 21
            </span>
            </br>
            Business Case
          `,
          text: "Plans in Place and Business Case Developed",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q18",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "The Organisation has no formal business case prepared with regards to implementing changes relating to ESG matters.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "The Organisation has some individual business plans in place for individual activities, but no comprehensive overall business plan across the whole Organisation.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "The Organisation has a high-level business case in place for its ESG programme which it monitors periodically.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "The Organisation has a clear understanding of the business case for ESG and has a formal, approved business plan, with individual business cases prepared for each area of business change. This business plan and the individual business cases are monitored, reported and updated periodically.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q19.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 19 of 21
            </span>
            </br>
            Business Case
          `,
          text: "Business Case Elements",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q19",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "The Organisation does not have a clear business case and does not understand how ESG can present opportunities to enhance revenue, reduce costs, improve risk management, etc.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "The Organisation has some individual business cases on ESG matters in place which focus on implementation without connection to revenue generation, cost reduction, employee engagement, risk mitigation etc. aspects of the plan.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "The Organisation has a clear understanding of the business case for ESG at an overall level but has not separated out individual components of revenue generation, cost reduction, employee engagement, etc.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "The Organisation has a clear understanding of the business case for ESG, with ESG activities clearly defined for areas of providing revenue growth, helping to reduce costs, helping to manage risk, beneficial to employee engagement, etc.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q20.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 20 of 21
            </span>
            </br>
            ESG Programme
          `,
          text: "Contains all Required Elements of E, S and G identified by the Organisation Stakeholders",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q20",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "There is no comprehensive ESG programme within the Organisation.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "The Organisation has a limited plan in place that deals with only some limited aspects of E, S and G.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "The Organisation has a solid ESG programme in place across some elements of E,S and G which the Organisation has deemed as essential to the programme.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "The Organisation has a comprehensive ESG programme in place that covers appropriately and completely all areas of E, S and G that have been determined to be of importance to the Organisation and its stakeholders based on the ESG strategy.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/q21.jpeg'),
          group: `
            <span style="font-weight: normal; padding-bottom: 10px; display: inline-block;" class="grey--text text--lighten-1">
              Question 21 of 21
            </span>
            </br>
            ESG Programme
          `,
          text: "Comprehensive Set of Considerations",
          background: "",
          footer: "",
          isVisible: true,
          fields: [
            {
              id: "Q21",
              vComponent: "v-radio",
              label: "Please select the statement that best describes the current situation in your organisation",
              rules: [ v => !!v || "This field is required." ],
              isVisible: true,
              isCached: false,
              options: [
                {
                  value: "a",
                  label: "There is no comprehensive ESG programme within the Organisation. Activities and assessments are ad hoc and do not cover the full range of anticipated ESG considerations.",
                  imageSrc: ""
                },
                {
                  value: "b",
                  label: "The Organisation has a limited ESG programme that does not cover many of the aspects of product development, communications, green financing, supply chain issues, Net Zero, employee engagement etc.",
                  imageSrc: ""
                },
                {
                  value: "c",
                  label: "The ESG programme of the Organisation contains some essential and key aspects of a \"standard\" ESG programme based on a strategic assessment of what is required, in areas such as new products, communications, green financing, supply chain issues, Net Zero, employee engagement etc.",
                  imageSrc: ""
                },
                {
                  value: "d",
                  label: "At a minimum, the ESG programme includes assessments and activities concerning (where appropriate) customers, new product development, internal and external communications, possible green financing, possible use of grants and incentives, supply chain implications, regulatory compliance considerations and employee engagement.",
                  imageSrc: ""
                },
              ],
            }
          ],
        },
        {
          imageSrc: require('./assets/doubter.jpeg'),
          group: "Result",
          text: `
            <p class="text-h3 mb-8 pr-4" style="font-family: inherit!important;">Beginner</p>
            <p class="text-subtitle-1 pr-4" style="font-family: inherit!important;">You’re in the beginning stages of developing a sustainability transformation plan for your organisation. But by taking this survey, you’ve shown you’re ready to get started.</p>
            <p class="text-subtitle-1 pr-4" style="font-family: inherit!important;">What can you do to progress to the next level? Start integrating ESG into the core of your business by creating a governance structure and defining clear targets and goals. Research how your business is impacted by ESG regulations and create a path to report around ESG matters.</p>
            <p class="text-subtitle-1 pr-4" style="font-family: inherit!important;">Not sure where to begin? We can help you take the next step. Contact us to see how we can help you map the path from Beginner all the way to Champion.</p>
          `,
          background: "primary",
          isVisible: () => this.mostFrequentAnswer === 'a',
          fields: [],
        },
        {
          imageSrc: require('./assets/pragmatist.jpeg'),
          group: "Result",
          text: `
            <p class="text-h3 mb-8 pr-4" style="font-family: inherit!important;">Pragmatist</p>
            <p class="text-subtitle-1 pr-4" style="font-family: inherit!important;">You have taken the first steps to integrate ESG topics into your business strategy and organisation. By taking this test, you’ve shown you’re ready to take your commitments to the next level.</p>
            <p class="text-subtitle-1 pr-4" style="font-family: inherit!important;">How can you get from Pragmatist to Strategist, or even Champion? Consider hiring a Chief Sustainability Officer to clearly define your goals and targets, and frequently report back to the Board. Create a carbon footprint reduction plan and understand your obligations for internal and external reporting.</p>
            <p class="text-subtitle-1 pr-4" style="font-family: inherit!important;">Not sure where to begin? We can help you take the next step. Contact us to see how we can help you map the path from Pragmatist all the way to Champion.</p>
          `,
          background: "primary",
          isVisible: () => this.mostFrequentAnswer === 'b',
          fields: [],
        },
        {
          imageSrc: require('./assets/strategist.jpeg'),
          group: "Result",
          text: `
            <p class="text-h3 mb-8 pr-4" style="font-family: inherit!important;">Strategist</p>
            <p class="text-subtitle-1 pr-4" style="font-family: inherit!important;">You are already far along your transformation to a sustainable business. ESG topics are a core part of your business model and operations, and you have comprehensive goals and targets for how to reduce your carbon footprint.</p>
            <p class="text-subtitle-1 pr-4" style="font-family: inherit!important;">There’s just a few small steps to take before you become a true Champion that make sustainability the main driver of their purpose. You can start by dedicating a Chief Sustainability Officer to establish a comprehensive plan and set of deadlines to achieve your targets. Consider establishing a subcommittee of your Board and increase your internal and external communication to demonstrate your commitment to these efforts.</p>
            <p class="text-subtitle-1 pr-4" style="font-family: inherit!important;">For further guidance on how to take the next steps in transforming your business, contact us. We can help you map the path from Strategist to Champion and beyond.</p>
          `,
          background: "primary",
          isVisible: () => this.mostFrequentAnswer === 'c',
          fields: [],
        },
        {
          imageSrc: require('./assets/idealist.jpeg'),
          group: "Result",
          text: `
            <p class="text-h3 mb-8 pr-4" style="font-family: inherit!important;">Champion</p>
            <p class="text-subtitle-1 pr-4" style="font-family: inherit!important;">Congratulations, you’re a champion of sustainability! It is the main driver of your purpose and at the core of all your business plans. But the work isn’t over. It’s important to continue to monitor your progress and keep building on this momentum.</p>
            <p class="text-subtitle-1 pr-4" style="font-family: inherit!important;">Reach out to have a conversation on how to ensure your business continues to create value and build trust for business, economy and society.</p>
          `,
          background: "primary",
          isVisible: () => this.mostFrequentAnswer === 'd',
          fields: [],
        },
      ].map((q, i) => ({ ...q, id: `${Math.random().toString(36).substr(2, 9)}${i}` }))
    },
    visibleQuestions() {
      return this.questions.filter(question => {
        if (typeof question.isVisible === 'function') {
          return question.isVisible(this.questionsAnswers)
        } else {
          return question.isVisible
        }
      });
    },
    visibleQuestionsImages() {
      return this.visibleQuestions.filter((question, index, self) =>
        index === self.findIndex(q => q.imageSrc === question.imageSrc)
      )
    },
    fields() {
      return this.questions.reduce((a, q) => [...a, ...q.fields], [])
    },
    imagesSplideOptions() {
      if (this.windowInnerWidth < this.mobileBreakpoint) {
        return {
          drag: false,
          arrows: false,
          pagination: false,
          keyboard: false,
          speed: 900,
          easing: "cubic-bezier(0.76, 0, 0.24, 1)",
          height: "50vw",
          direction: "ltr",
          type: 'loop',
          lazyLoad: 'nearby'
        }
      } else {
        return {
          drag: false,
          arrows: false,
          pagination: false,
          keyboard: false,
          speed: 900,
          easing: "cubic-bezier(0.76, 0, 0.24, 1)",
          height: "45vw",
          direction: "ttb",
          type: 'loop',
          lazyLoad: 'nearby'
        }
      }
    },
    questionsSplideOptions() {
      if (this.windowInnerWidth < this.mobileBreakpoint) {
        return {
          drag: false,
          arrows: false,
          pagination: false,
          keyboard: false,
          speed: 900,
          easing: "cubic-bezier(0.76, 0, 0.24, 1)",
          height: "0", // 180vw
          direction: "ltr",
          type: 'loop'
        };
      } else {
        return {
          drag: false,
          arrows: false,
          pagination: false,
          keyboard: false,
          speed: 900,
          easing: "cubic-bezier(0.76, 0, 0.24, 1)",
          height: "45vw",
          direction: "ttb",
          type: 'loop'
        };
      }
    },
    previousQuestion() {
      if (this.visibleQuestions[this.$refs.questionsSplide.index - 1]) {
        return this.visibleQuestions[this.$refs.questionsSplide.index - 1]
      } else {
        return this.visibleQuestions[this.$refs.questionsSplide.length - 1]
      }
    },
    currentQuestion() {
      return this.visibleQuestions[this.$refs.questionsSplide.index]
    },
    nextQuestion() {
      if (this.visibleQuestions[this.$refs.questionsSplide.index + 1]) {
        return this.visibleQuestions[this.$refs.questionsSplide.index + 1]
      } else {
        return this.visibleQuestions[0]
        }
    },
  },
  created() {
    this.questions.forEach(q => q.fields.forEach(f => {
      Vue.set(this.questionsAnswers, f.id, null)
    }))
    this.windowInnerWidth = window.innerWidth
    window.addEventListener('resize', () => {
      console.log(window.innerWidth, window.innerHeight)
      this.windowInnerWidth = window.innerWidth
    })
  },
  methods: {
    startQuiz() {
      this.$refs.forms.forEach(form => form.resetValidation())
      this.id = new Date().getTime().toString();
      this.questions.forEach(q => q.fields.forEach(f => {
        if (!f.isCached) {
          Vue.set(this.questionsAnswers, f.id, null)
        }
      }))
      this.nextSlide()
    },
    checkResult() {
      this.nextSlide()
      this.submitResponse()
    },
    nextSlide() {
      this.updateMostFrequentAnswer()
      this.refreshSplides()
      this.isSliding = true
      let moveImagesSplide = true
      if (this.currentQuestion && this.nextQuestion) {
        moveImagesSplide = this.currentQuestion.imageSrc !== this.nextQuestion.imageSrc
      }
      this.updateSlideHeight(this.$refs.questionsSplide.index + 1)
      this.$refs.questionsSplide.go("+1", true)
      this.updateQuizProgress()
      if (moveImagesSplide) {
        setTimeout(() => {
          this.$refs.imagesSplide.go("+1", true)
          setTimeout(() => {
            this.isSliding = false
          }, this.splideOptions.speed)
        }, this.splideDelay)
      } else {
        this.isSliding = false
      }
    },
    previousSlide() {
      this.updateMostFrequentAnswer()
      this.refreshSplides()
      this.isSliding = true
      let moveImagesSplide = true
      if (this.currentQuestion && this.previousQuestion) {
        moveImagesSplide = this.currentQuestion.imageSrc !== this.previousQuestion.imageSrc
      }
      this.updateSlideHeight(this.$refs.questionsSplide.index - 1)
      if (moveImagesSplide) {
        this.$refs.imagesSplide.go("-1", true)
      }
      setTimeout(() => {
        this.$refs.questionsSplide.go("-1", true);
        this.updateQuizProgress()
        setTimeout(() => {
          this.isSliding = false;
        }, this.splideOptions.speed)
      }, this.splideDelay)
    },
    submitResponse() {
      let columns = this.fields.map(f => f.id)
      let answers = this.fields.map(f => 
        Array.isArray(this.questionsAnswers[f.id]) ? this.questionsAnswers[f.id].join(";") : this.questionsAnswers[f.id]
      );
      
      let xhr = new XMLHttpRequest();
      xhr.open("POST", process.env.BASE_URL + "save-response.php");
      xhr.setRequestHeader("Content-type", "application/x-www-form-urlencoded; charset=utf-8");
      xhr.send("r=" + JSON.stringify({ columns, answers, result: this.mostFrequentAnswer, id: this.id }));
    },
    refreshSplides() {
      this.$refs.questionsSplide.splide.refresh()
      this.$refs.imagesSplide.splide.refresh()
    },
    updateMostFrequentAnswer() {
      let arr = Object.values(this.questionsAnswers).filter(v => v)
      this.mostFrequentAnswer = Object.entries(
        arr.reduce((a, v) => {
          a[v] = a[v] ? a[v] + 1 : 1;
          return a;
        }, {})
      ).reduce((a, v) => (v[1] >= a[1] ? v : a), [null, 0])[0]
    },
    updateSlideHeight(slideIndex) {
      if (window.innerWidth > this.mobileBreakpoint) return;

      this.$nextTick(() => {
        let slides = this.$el.querySelectorAll("#questions .splide__slide");
        let i;

        for (i = 0; i < slides.length; i++) {
          slides[i].style.height = "100%";
        }

        let slide = slides[slideIndex];
        let slideContainer = slide.querySelector(".splide__slide__container");
        let height = 0;

        for (i = 0; i < slideContainer.childNodes.length; i++) {
          let node = slideContainer.childNodes[i];
          if (node.nodeType === Node.ELEMENT_NODE) {
            height += slideContainer.childNodes[i].offsetHeight;
          }
        }

        height += 200;

        slide.style.height = "100%";
        slide.parentNode.style.height = height + "px";
      });
    },
    updateQuizProgress() {
      this.progress = this.$refs.questionsSplide.index / (this.$refs.questionsSplide.length - 1) * 100
    },
  },
};
</script>

<style lang="scss">

#app * {
  font-family: 'Helvetica Neue', Arial, sans-serif!important;
  font-weight: normal;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
/* General */

.v-input--selection-controls__input {
  margin-right: 15px;
}

.v-input--radio-group--column .v-input--radio-group__input>.v-label {
  padding-bottom: 30px;
}

.v-label {
  font-family: Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-size: 15px;
  line-height: 1.6;
}

.v-radio {
  align-items: flex-start;
}

.v-input--radio-group--column .v-radio:not(:last-child):not(:only-child) {
  margin-bottom: 20px;
}

textarea {
    font-size: 1rem;
}

ul {
    list-style: square;
    padding-left: 15px;
}

ul li {
    font-size: inherit;
}

ol.list-with-icons, 
ul.list-with-icons {
    //padding: 0 5vw 0 5vw;
    padding: 0 5vw 0 0;
    list-style-type: none;
}
ol.list-with-icons li, 
ul.list-with-icons li {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 2vw 0 0;
}
ol.list-with-icons li svg,
ul.list-with-icons li svg {
    width: 3.5vw;
    height: 3.5vw;
    margin-right: 2vw;
}
ol.list-with-icons li svg *,
ul.list-with-icons li svg * {
    fill: #fff;
}

@media (max-width: 768px) {

    ol {
        padding: 15px 0 5px 70px;
    }
    ol li {
        margin-bottom: 25px;
    }
    ol li, li p {
        font-size: 2.6vw;
    }
    li svg {
        width: 8vw;
        height: 8vw;
        left: -65px;
        top: 4px;
    }
    ol.list-with-icons li,
    ul.list-with-icons li {
        margin: 25px 0 0;
    }
    ol.list-with-icons li svg,
    ul.list-with-icons li svg {
        width: 50px;
        height: 50px;
        margin-right: 25px;
    }
    ol.list-with-icons + .splide__slide__navigation {
        margin-top: 20px;
    }
}

.quiz-selector__heading {
    font-size: 3.2vw;
    font-weight: bold;
    margin: 0 0 2.5vw;
}
.quiz-selector__link {
    font-size: 1.55vw;
    margin: 1.2vw 0;
}
.quiz-selector__link:hover {
    text-decoration: underline;
}
.quiz-selector__link:last-of-type {
    margin-bottom: 3.5vw;
}
.link-with-arrow {
    position: relative;
    cursor: pointer;
}

@media (min-width: 769px) {

    .link-with-arrow:hover {
        &::after {
           right: -3vw; 
        }
    }
    .link-with-arrow::after {
        content: "\25B2";
        transform: rotate(90deg) scale(0.9);
        position: absolute;
        top: 0;
        right: -1.75em;
        font-size: inherit;
    }
    .link-with-arrow .arrow {
        display: none;
    }
}

@media (max-width: 768px) {

    .quiz-selector__heading {
        font-size: 3.2rem;
        margin: 0 0 1rem;
    }
    .quiz-selector__link {
        font-size: 6vw;
        margin: 35px 0 0;
    }
    .quiz-selector__link:last-of-type {
        margin-bottom: 2vw;
    }
    .link-with-arrow .arrow {
        display: inline-block;
        margin-left: 10px;
        transform: rotate(90deg) scale(0.9);
    }
}

/* #region Quiz results */
.quiz-results__wrapper {
    margin: 0;
    position: relative;
    
    .btn {
        margin-top: 1.8vw;
        margin-right: 10px;
    }

    &.quiz-results__wrapper--border {
        border: 4px solid white; 
        padding: 0.4vw 2.2vw 2vw 2.2vw; 
        margin: 2.2vw 0 1.6vw;
    }
}
.quiz-results__wrapper:first-of-type {
    margin: 8vw 0 0;

    &::after {
        content: "\201D";
        font-family: "PwC Helvetica Neue", Arial, Sans-Serif;    
        color: #e0301e;
        font-size: 18vw!important;
        left: -1vw!important;
        top: -2vw!important;
        position: absolute;
    }
}

@media (max-width: 768px) {

    .quiz-results__wrapper.quiz-results__wrapper--border {
        border: 4px solid white; 
        padding: 0 20px 20px; 
        margin: 20px 0;
    }
    .quiz-results__wrapper .btn {
        margin-top: 20px;
    }
}
/* #endregion Quiz results */

/* #region Questions */
.question__number {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 1vw;
    font-weight: bold;
    line-height: 1.6;
    margin: 0 0 0.85vw;
    width: 100%;
}
.questions__length {
    font-size: 0.8vw;
}
.question__text {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 1.5vw;
    line-height: 1.6;
    margin: 1.2vw 0 0;
    width: 100%;

    &:first-of-type {
        margin: 1.4vw 0 0!important;
    }
  
    &.question__text--small {
        font-size: 1vw;
        line-height: 1.8;
        margin: 1.6vw 0 0;

        ul {
            margin-top: .9vw;
        }
    }

    &.question__text--medium {
        font-size: 1.4vw;
        line-height: 1.8;
        margin: 1.6vw 0 0;

        ul {
            margin-top: .9vw;
        }
    }

    &.question__text--large {
        font-size: 2vw;
    }
}
.question__text:first-of-type {
    margin: 0;
}
.question__answer__options {
    margin: 20px 0 0;

    textarea {
        width: 100%;
        margin-top: 10px;
    }
}

@media (max-width: 768px) {
    
    .question__text {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 1.6rem;
        margin-top: 15px;

        &:first-of-type {
            margin: 20px 0 0!important;
        }
    }
    .question__number {
        font-size: 1.2rem;
    }
    .question__text--small {
        font-size: 1.25rem;
        margin-top: 25px;
    }
    .question__text--small + .splide__slide__navigation {
        margin-top: 20px;
    }

    .question__text.question__text--medium {
        font-size: 1.4rem;
        margin-top: 25px;
    }
}
/* #endregion */

/* #region Hero promo */
.hero-promo {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 5vw;
    height: 100%;
    min-height: unset;
    background: transparent;
}
.hero-promo .hero-promo__title {
    position: static;
    margin-bottom: 20vw;
}
.hero-promo .hero-promo__title-textpanel {
    padding: 0;
    margin: 0;
    background: transparent;
}
.hero-promo .hero-promo__title-heading {
    font-size: 3vw;
}

@media (max-width: 768px) {
    
    .hero-promo .hero-promo__title {
        position: static;
        margin-bottom: 0;
    }
    .hero-promo .hero-promo__title-heading {
        font-size: 6.5vw;
    }
    .hero-promo .hero-promo__title-abstract {
        font-size: 5vw;
    }
}
/* #endregion */

/* #region Options */
.option__container {
  display: block;
  position: relative;
  padding-left: 35px;
  margin: 20px 20px 18px 0;
  cursor: pointer;
  font-size: 15px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  line-height: 1.4;
}
.option__container input[type="checkbox"],
.option__container input[type="radio"] {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}
.checkmark {
    position: absolute;
    top: 0;
    left: 0;
    background-color: #eee;
    height: 20px;
    width: 20px;
}
.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}
.checkmark--radio {
  border-radius: 50%;
}
.option__container:hover input ~ .checkmark {
  background-color: #ccc;
} 
.option__container input:checked ~ .checkmark {
  background-color: #e0301e;
}
.option__container input:checked ~ .checkmark:after {
  display: block;
}
.option__container .checkmark--radio:after {
  top: 7px;
  left: 7px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: white;
}
.option__container .checkmark--checkbox:after {
  left: 8px;
  top: 4px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2.5px 2.5px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}
/* #endregion */

/* #region Splide */
#images {
    background-color: #404041;
}
.splide {
    margin: 0;
    padding: 0;
}
.splide__slide {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    overflow-y: auto;
    margin: 0;
}
.splide__slide-cover {
    height: 100%;
    display: none;
    transition: opacity .3s ease;
    background-color: #404041;
    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
}
.splide__slide-cover > img {
    height: 80%;
}
.splide__slide__container {
    padding: 5vw;
    width: 100%;
    display: flex;
    flex-flow: column;
    justify-content: center;
    align-items: flex-start;
}
.splide__slide__navigation {
    margin: 1.1vw 0 0;
    width: 100%;
}
.splide__slide__navigation > .btn {
    transition: all .2s ease;
    opacity: .2;
    pointer-events: none;
}
.splide__slide__navigation > .btn.active {
    opacity: 1;
    pointer-events: all;
}
// .splide__slide-cover.active {
//     display: block;
// }
.splide__slide--primary {
    background: #e0301e;
    color: #fff;
}
.splide__slide--secondary {
    background: #000;
    color: #fff;
}
.question__answer__options {
    width: 100%;
}
.btn--primary {
    background: #e0301e;
}
.btn--primary:hover {
    background: #c92b1b;
}
.btn--transparent {
    background: transparent!important;
    border: 1px solid #fff!important;
    color: #fff!important;
    margin: 10px 0;
}
.btn--transparent:hover {
    background: #fff!important;
    border: 1px solid #fff!important;
    color: #2d2d2d!important;
}

@media (max-width: 768px) {
    
    .splide__slide__container {
        justify-content: flex-start;
        padding-top: 10vw;
    }
    .splide__slide {
        align-items: flex-start;
    }
    .splide__slide-cover {
        background-size: 65%;
    }
}
/* #endregion */

/* #region Vue Multiselect */
.multiselect__tag {
    color: #2d2d2d;
    background: #cccccc;
    font-size: 0.9rem;
    padding: 0px 20px 0px 6px;
    margin: 8px 0px 0px 4px;
}

.multiselect__option {
    min-height: unset;
    padding: 2px 8px!important;
}

.multiselect__option--highlight {
    background: #f2f2f2;
    color: #2d2d2d;
}

.multiselect__tag-icon {
    color: #2d2d2d;
}

.multiselect__tag-icon:focus, .multiselect__tag-icon:hover {
    background: none;
}

.multiselect__single,
.multiselect__element {
    padding: 6px!important;
    font-size: 1rem!important;
    margin: 0;
}
/* #endregion */

/* #region Sh territory select */
.sh-territory-sel {
    color: #fff;
    background: transparent;
    width: 100%;
    padding: 2vw 0 0 0;
}
.sh-territory-sel .sh-territory-sel__select {
    padding-top: 12px;
}
.sh-territory-sel:first-of-type {
    padding: 0;
}
.sh-territory-sel .sh-territory-sel__text {
    font-size: 0.9vw;
    margin-bottom: 0;
}
.sh-territory-sel .sh-territory-sel__select-display {
    font-size: 1.4vw;
    padding: 0 0 0.9vw;
    background: #2d2d2d;
}
.sh-territory-sel .sh-territory-sel__select {
    border-bottom-color: #fff;
}
.sh-territory-sel .sh-territory-sel__select:hover {
    border-bottom-color: #e0301e;
}
.sh-territory-sel .sh-territory-sel__select:focus {
    border-bottom-color: #fff;
    box-shadow: none;
}
.sh-territory-sel .sh-territory-sel__icon {
    width: 2vw;
    height: 2vw;
}
.sh-territory-sel .sh-territory-sel__icon svg {
    fill: #fff;
}
.sh-territory-sel .sh-territory-sel__select:hover ~ .sh-territory-sel__icon svg {
    fill: #e0301e;
}
.sh-territory-sel .sh-territory-sel__select:focus ~ .sh-territory-sel__icon svg {
    fill: #fff;
} 
.sh-territory-sel .sh-territory-sel__select:hover + .sh-territory-sel__select-display {
    color: #e0301e
} 
.sh-territory-sel .sh-territory-sel__select:focus + .sh-territory-sel__select-display {
    color: #fff;
}
@media (max-width: 768px) {
    .sh-territory-sel {
        padding: 30px 0 0 0;
    }
    .sh-territory-sel .sh-territory-sel__select {
        padding: 15px 0;   
    }
    .sh-territory-sel .sh-territory-sel__select-display {
        font-size: 1.5rem;
        padding: 10px 0 12px;
    }
    .sh-territory-sel .sh-territory-sel__text {
        font-size: 1.2rem;
    }
    .sh-territory-sel .sh-territory-sel__icon {
        width: 30px;
        height: 30px;
    }
    .sh-territory-sel .sh-territory-sel__icon > svg {
        width: 25px;
        height: 25px;
    }
}
/* #endregion */

}

/* #region Materialize Tooltip */
.material-tooltip {
    padding: .8vw 1vw;
    margin: 0 15px;
    font-size: 0.9vw;
    z-index: 2000;
    background-color: transparent;
    color: #fff;
    min-height: 36px;
    line-height: 1.25;
    opacity: 0;
    position: absolute;
    text-align: left;
    max-width: 25vw;
    overflow: hidden;
    top: 0;
    left: 0;
    pointer-events: none;
    visibility: hidden;
    background-color: #e0301e;
    box-shadow: 0 14px 28px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22);
}
.material-tooltip {
    position: absolute;
}
.material-tooltip .tooltip-content {
    font-family: "PwC Helvetica Neue", Arial, Sans-Serif;
}
.tooltipped {
    cursor: help;
    position: relative;
    width: 20px;
    height: 20px;
    display: inline-block;
}
.tooltipped::after {
    content: "?";
    color: transparent;
    // background-image: url('../images/hint-icon.svg');
    background-size: cover;
    background-repeat: no-repeat;
    width: 1.2vw;
    height: 1.2vw;
    margin: 0 .3vw 0 0;
    top: 0;
    left: 0;
    display: inline-block;
    position: absolute;
}
.question__answer__options .tooltipped::after {
    margin-left: 8px;
}

@media (max-width: 768px) {

    .tooltipped::after {
        width: 18px;
        height: 18px;
        margin: 0;
    }
    .material-tooltip {
        font-size: 1.4rem;
        line-height: 1.35;
        max-width: 100%;
        margin: 0 15px;
        padding: 18px 20px;
    }
}
/* #endregion */
</style>
